import { ColumnWidthOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OscarSuggestion } from '../../types/AIAgentTypes';
import OscarComposer from './OscarComposer';
import OscarContextBar from './OscarContextBar';
import OscarEmptyState from './OscarEmptyState';
import OscarPanelHeader from './OscarPanelHeader';
import { useOscarTheme } from '../../context/OscarRoleContext';
import { OSCAR_PANEL_MIN_WIDTH, useOscarPanelWidth } from '../../hooks/useOscarPanelWidth';

interface OscarPanelProps {
	/** First name shown in the greeting, e.g. "XYZ" in "Hello XYZ,". */
	userName: string;
	title?: string;
	suggestions?: OscarSuggestion[];
	/** Defaults to loading the suggestion into the composer so the user can edit before sending. */
	onSuggestionClick?: (suggestion: OscarSuggestion) => void;

	/**
	 * The conversation. When omitted the empty state renders instead — there is
	 * no message-bubble design yet, so the transcript is supplied by the host.
	 */
	children?: React.ReactNode;

	/**
	 * Context bar, pinned directly above the composer. Omit `contextSummary` to
	 * hide the strip entirely.
	 */
	contextSummary?: React.ReactNode;
	contextToggleLabel?: string;
	contextToggleChecked?: boolean;
	onContextToggleChange?: (checked: boolean) => void;
	/** Defaults to collapsed (Figma 15111:53235) — controls hidden, chevron down. */
	contextCollapsed?: boolean;
	onToggleContextCollapsed?: () => void;

	/**
	 * Connection strip, pinned between the context bar and the composer — this is
	 * where `OscarConnectionState` goes. Kept as a slot rather than a status
	 * prop so the panel stays presentational and knows nothing about the harness.
	 */
	statusStrip?: React.ReactNode;

	/** Composer. `onSendMessage` receives the trimmed draft; the draft is cleared after. */
	onSendMessage?: (message: string) => void;
	/** Blocks the composer while the agent is unreachable. */
	composerDisabled?: boolean;
	composerPlaceholder?: string;
	sending?: boolean;
	/** Turns the send button into a stop button while `sending`. */
	onCancelMessage?: () => void;
	/** Pinned above the composer input, inside its bordered region. */
	composerPrompt?: React.ReactNode;

	/** Header actions — each is hidden when its handler is omitted. */
	onNewChat?: () => void;
	onViewHistory?: () => void;
	onExpand?: () => void;
	onClose?: () => void;

	/**
	 * Adds drag handles — the leading edge and a resize button at its top. Off for
	 * the fullscreen route.
	 */
	resizable?: boolean;
	/** Positioning is the host's job; the panel fills whatever box it is given. */
	className?: string;
}

/**
 * Figma node 14998:125297 — the Oscar side panel: a 420px column on the
 * purple surface, with a white bordered card holding the conversation, the
 * sharing/context strip, and the composer.
 */
export const OscarPanel = ({
	userName,
	title,
	suggestions,
	onSuggestionClick,
	children,
	contextSummary,
	contextToggleLabel,
	contextToggleChecked,
	onContextToggleChange,
	contextCollapsed,
	onToggleContextCollapsed,
	statusStrip,
	onSendMessage,
	composerDisabled,
	composerPlaceholder,
	sending,
	onCancelMessage,
	composerPrompt,
	onNewChat,
	onViewHistory,
	onExpand,
	onClose,
	resizable,
	className,
}: OscarPanelProps) => {
	const theme = useOscarTheme();
	const { width, dragging, columnRef, handlePointerDown, handleKeyDown } = useOscarPanelWidth();
	const [draft, setDraft] = useState('');

	const handleSubmit = (message: string) => {
		// Nothing to clear if nothing took it. This is an exported component, and
		// a consumer that omits the handler would otherwise lose the typed text on
		// every Enter with no sign of where it went.
		if (!onSendMessage) {
			return;
		}
		onSendMessage(message);
		setDraft('');
	};

	const handleSuggestionClick = (suggestion: OscarSuggestion) => {
		if (onSuggestionClick) {
			onSuggestionClick(suggestion);
			return;
		}
		setDraft(suggestion.label);
	};

	return (
		<div
			className={cn(
				// The 3-layer shadow has no design-system token — see DESIGN_TOKEN_GAPS.md.
				'flex h-full items-center border-0 border-l-2 border-solid px-xs pb-2.5 pt-0 shadow-[0_9px_28px_8px_rgba(0,0,0,0.05),0_6px_16px_0_rgba(0,0,0,0.08),0_3px_6px_-4px_rgba(0,0,0,0.12)]',
				theme.surface,
				theme.panelEdge,
				resizable && 'relative',
				className
			)}
		>
			{resizable ? (
				<div
					role="separator"
					aria-orientation="vertical"
					aria-label="Resize panel"
					aria-valuenow={width}
					aria-valuemin={OSCAR_PANEL_MIN_WIDTH}
					tabIndex={0}
					onPointerDown={handlePointerDown}
					onKeyDown={handleKeyDown}
					// Sits on the panel's leading edge, a little wider than it looks so it
					// is not a pixel hunt. Drag left to widen, or use the arrow keys.
					// Arbitrary values, not `-translate-x-1/2`: preflight is off, so the
					// --tw-* transform variables are never defined and every transform
					// utility in the app silently does nothing. The handle was sitting
					// on the panel edge instead of straddling it.
					className="absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize [transform:translateX(-50%)]
						before:absolute before:inset-y-0 before:left-1/2 before:w-px before:bg-transparent
						before:[transform:translateX(-50%)] hover:before:bg-grey-5 focus-visible:outline-none
						focus-visible:before:bg-grey-6"
				/>
			) : null}
			{resizable ? (
				// The edge handle is a few pixels nobody finds; this is the one people
				// see. Same control as `ResizableSplitPanel`, straddling the edge near
				// the top. Same drag and keys: wider to the left, down to the minimum.
				<Tooltip title="Drag to resize" placement="left">
					<Button
						type={dragging ? 'primary' : 'default'}
						shape="circle"
						icon={<ColumnWidthOutlined />}
						aria-label="Resize panel"
						onPointerDown={handlePointerDown}
						onKeyDown={handleKeyDown}
						// `!` on position and cursor: antd's stylesheet loads after Tailwind and
						// `.ant-btn` sets `position: relative` and `cursor: pointer`, which put
						// the button back in flow — centred by the panel's `items-center` and
						// taking up room. Top aligns it with the header row. Arbitrary
						// transform for the same preflight reason as the edge handle.
						className="!absolute left-0 top-s z-20 !cursor-col-resize touch-none [transform:translateX(-50%)]"
					/>
				</Tooltip>
			) : null}
			<div
				ref={columnRef}
				style={resizable ? { width } : undefined}
				// `max-w-full`: the dock is full width on phones, narrower than the
				// panel's 420px minimum, so the stored width must give way to it.
				className={cn(
					'flex h-full max-w-full flex-col items-center',
					resizable ? null : 'w-[420px]'
				)}
			>
				<OscarPanelHeader
					title={title}
					onNewChat={onNewChat}
					onViewHistory={onViewHistory}
					onExpand={onExpand}
					onClose={onClose}
				/>
				<div
					className={cn(
						'flex min-h-0 w-full flex-1 flex-col rounded border-2 border-solid bg-white',
						theme.panelCardBorder
					)}
				>
					{children ? (
						<div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">{children}</div>
					) : (
						<OscarEmptyState
							greeting={`Hello ${userName},`}
							suggestions={suggestions}
							onSuggestionClick={handleSuggestionClick}
						/>
					)}
					{contextSummary ? (
						<OscarContextBar
							summary={contextSummary}
							toggleLabel={contextToggleLabel}
							toggleChecked={contextToggleChecked}
							onToggleChange={onContextToggleChange}
							collapsed={contextCollapsed}
							onToggleCollapsed={onToggleContextCollapsed}
						/>
					) : null}
					{statusStrip}
					<OscarComposer
						value={draft}
						onChange={setDraft}
						onSubmit={handleSubmit}
						placeholder={composerPlaceholder}
						disabled={composerDisabled}
						sending={sending}
						onCancel={onCancelMessage}
						prompt={composerPrompt}
					/>
				</div>
			</div>
		</div>
	);
};

export default OscarPanel;
